import Vue from 'vue'
import Router from 'vue-router'
import createAsyncRoutes from './asyncRoutes'
import store from './store'
import { UPDATE_USER_INFO } from './store/mutation-types'
import { Message } from 'element-ui'
import * as utils from '@/utils'
import * as api from 'index/api'

Vue.use(Router)

const nploading = new utils.Loading({ delay: 1000 })

export const routes = [
  {
    path: '/',
    redirect: '/project'  // 重定向到项目列表
  },
  {
    path: '/forms',  // 保留旧版问卷管理作为备用入口
    name: 'navigator',
    meta: {
      title: '明暗检问卷系统'
    },
    component: () => import('./views/navigator')
  },
  /* {
    path: '/',
    name: 'login',
    component: () => import('./views/login')
  }, */
  {
    path: '/login',
    name: 'login',
    meta: {
      title: '登录'
    },
    component: () => import('./views/login')
  },
  // 全屏阅卷页面（独立于项目布局之外）
  {
    path: '/grading',
    name: 'grading',
    meta: {
      title: '数据审核'
    },
    component: () => import('./views/project/GradingPage')
  },
  // 全屏答卷预览页面（独立于项目布局之外）
  {
    path: '/answer-preview',
    name: 'answerPreview',
    meta: {
      title: '答卷详情'
    },
    component: () => import('./views/project/AnswerPreviewPage')
  },
  // 全屏申诉审核页面（独立于项目布局之外）
  {
    path: '/appeal-review',
    name: 'appealReview',
    meta: {
      title: '申诉审核'
    },
    component: () => import('./views/project/AppealReviewPage')
  },
  // 全屏申诉下发页面
  {
    path: '/appeal-distribute',
    name: 'appealDistribute',
    meta: {
      title: '申诉下发配置'
    },
    component: () => import('./views/project/appeal/Distribute')
  },
  // 全屏报告上传页面
  {
    path: '/file-dist-upload',
    name: 'fileDistUpload',
    meta: {
      title: '上传报告'
    },
    component: () => import('survey/views/project/FileDistUpload')
  },
  // 材料在线预览页面（免登录，供Excel链接直接跳转）
  {
    path: '/material-preview/:itemId',
    name: 'materialPreview',
    meta: {
      title: '材料预览'
    },
    component: () => import('./views/project/MaterialPreview')
  },
  {
    path: '*',
    name: '404',
    component: () => import('./views/error/404')
  }
]

const router = new Router({
  scrollBehavior: () => ({ y: 0 }),
  routes
})

const hasAnyAuths = () => store.state.allPermissions.length > 0

// 根据permissions中的project字段判断登录后应跳转的路径
const getLoginRedirectPath = () => {
  const permissions = store.state.allPermissions || []

  // 从permissions中提取所有project标签
  const allProjectTags = permissions
    .filter(p => p.project)
    .flatMap(p => p.project.split(',').map(tag => tag.trim().toLowerCase()))
  const projectTagsSet = [...new Set(allProjectTags)]

  // 规则1: 仅有bzmj权限，无survey权限 -> 跳转 /bzmj
  if (projectTagsSet.includes('bzmj') && !projectTagsSet.includes('survey')) {
    return '/bzmj'
  }

  // 规则2: 包含survey权限 -> 正常跳转 /project
  if (projectTagsSet.includes('survey')) {
    return '/project'
  }

  // 规则3: 兜底 - 检查是否有bzmj相关的uri
  const bzmjModule = permissions.find(p => p.uri && p.uri.includes('/bzmj'))
  if (bzmjModule) {
    return '/bzmj'
  }

  // 规则4: 根据第一个有效模块路径跳转
  if (permissions.length > 0) {
    const firstModule = permissions.find(p => p.uri && p.uri.startsWith('/'))
    if (firstModule) {
      return firstModule.uri
    }
  }

  // 默认跳转项目页
  return '/project'
}

// 是否已添加过路由
let routeIsAdded = false

const addRoutes = () => {
  // 根据路径生成对应的路由配置
  const routes = [
    {
      path: '',
      component: () => import('./components/Layout'),
      children: createAsyncRoutes('health', store.state.allPermissions)
    },
    {
      path: '/xunjian',
      component: () => import('./components/Layout'),
      children: createAsyncRoutes('xunjian', store.state.allPermissions, '/xunjian')
    },
    // survey 项目路由 - 使用新的 ProjectLayout，直接定义子路由不依赖权限
    {
      path: '/project',
      component: () => import('./components/ProjectLayout'),
      redirect: '/project/list',
      children: [
        {
          path: 'list',
          name: 'projectList',
          meta: { title: '项目列表', name: '项目列表', extra: true },
          component: () => import('./views/project/List')
        },
        {
          path: ':projectId',
          name: 'projectDetail',
          meta: { title: '项目详情', active: '/project', extra: true },
          component: () => import('./views/project/Detail'),
          redirect: to => ({ name: 'projectOverview', params: { projectId: to.params.projectId } }),
          children: [
            {
              path: 'overview',
              name: 'projectOverview',
              meta: { title: '概览', name: '概览', active: '/project', extra: true },
              component: () => import('./views/project/Overview')
            },
            {
              path: 'forms',
              name: 'projectForms',
              meta: { title: '我的问卷', name: '我的问卷', active: '/project', extra: true },
              component: () => import('./views/project/Forms')
            },
            {
              path: 'waves',
              name: 'projectWaves',
              meta: { title: '波次', name: '波次', active: '/project', extra: true },
              component: () => import('./views/project/Waves')
            },
            {
              path: 'data',
              name: 'projectData',
              meta: { title: '数据管理', name: '数据管理', active: '/project', extra: true },
              component: () => import('./views/project/Data')
            },
            {
              path: 'members',
              name: 'projectMembers',
              // 成员管理已合并到 Settings 页，重定向过去
              redirect: to => ({ name: 'projectSettings', params: { projectId: to.params.projectId } })
            },
            {
              path: 'settings',
              name: 'projectSettings',
              meta: { title: '项目设置', name: '设置', active: '/project', extra: true },
              component: () => import('./views/project/Settings')
            },
            {
              path: 'appeal',
              name: 'projectAppeal',
              meta: { title: '申诉处理', name: '申诉处理', active: '/project', extra: true },
              component: () => import('./views/project/Appeal')
            },
            {
              path: 'store-report',
              name: 'projectStoreReport',
              meta: { title: '单店报告', name: '单店报告', active: '/project', extra: true },
              component: () => import('survey/views/project/StoreReport')
            },
            {
              path: 'file-distribution',
              name: 'projectFileDistribution',
              meta: { title: '报告下发', name: '报告下发', active: '/project', extra: true },
              component: () => import('survey/views/project/FileDistribution')
            },
            {
              path: 'segment-config',
              name: 'projectSegmentConfig',
              // 环节配置已合并到 Settings 页，重定向过去
              redirect: to => ({ name: 'projectSettings', params: { projectId: to.params.projectId } })
            },
            {
              path: 'wave-report/:waveId',
              name: 'WaveReport',
              meta: { title: '波次报告', name: '波次报告', active: '/project', extra: true },
              component: () => import('./views/project/WaveReport')
            },
            {
              path: 'dashboard',
              name: 'projectDashboard',
              meta: { title: '执行看板', name: '执行看板', active: '/project', extra: true },
              component: () => import('./views/project/Dashboard')
            },
            {
              path: 'baseData',
              name: 'projectBaseData',
              meta: { title: '厂家基础信息', name: '基础信息', active: '/project', extra: true },
              component: () => import('./views/project/BaseData')
            },

          ]
        }
      ]
    },
    // 管理后台路由（仅管理员可见）
    {
      path: '/admin',
      component: () => import('./components/ProjectLayout'),
      children: [
        {
          path: 'transcode',
          name: 'transcodeAdmin',
          meta: { title: '转码队列', name: '转码队列', extra: true },
          component: () => import('./views/admin/TranscodeAdmin')
        }
      ]
    },
    {
      path: '/recycle',
      component: () => import('./components/ProjectLayout'),
      children: [
        {
          path: '',
          name: 'recycleBin',
          meta: { title: '回收站', name: '回收站', extra: true },
          component: () => import('./views/recycle/index')
        }
      ]
    },
    {
      path: '/settings',
      component: () => import('./components/ProjectLayout'),
      children: [
        {
          path: '',
          name: 'appSettings',
          meta: { title: '系统设置', name: '设置', extra: true },
          component: () => import('./views/settings/index')
        }
      ]
    },

    {
      path: '/change-password',
      component: () => import('./components/Layout'),
      children: [
        {
          path: '',
          name: 'changePassword',
          meta: {
            title: '修改密码',
            attach: '/'
          },
          component: () => import(/* webpackChunkName: "password" */ '@/components/Password')
        }
      ]
    }
  ]
  router.addRoutes(routes)
  routeIsAdded = true
}

router.beforeEach((to, from, next) => {
  // 开启进度条
  nploading.start()
  // 改变文档标题
  const { title, name } = to.meta || {}
  if (title) {
    document.title = name || title
  }
  // 公开页面白名单：无需登录即可访问
  if (to.path === '/answer-preview' || to.path === '/login' || to.path.startsWith('/material-preview')) {
    next()
    nploading.done()
    return
  }
  // 防止在未登录的情况下去访问非登录页出现404的情况
  if (to.name === '404' && routeIsAdded === false) {
    store
      .dispatch(UPDATE_USER_INFO)
      .then(() => {
        if (store.getters.userInfo.uid) {
          if (hasAnyAuths()) {
            if (routeIsAdded === false) addRoutes()
            next({ path: to.path || '/', query: to.query, replace: true })
          } else {
            next({ path: '/login' })
          }
        } else {
          next({ path: '/login' })
        }
        nploading.done()
      })
      .catch(err => {
        console.error(err)
        next({ path: '/login' })
        nploading.done()
      })
    return
  }

  // 有权限的情况
  if (hasAnyAuths()) {
    if (to.path === '/login' && to.query.type !== 'reset') {
      next({ path: from.path || '/', query: to.query, replace: true })
      nploading.done()
    } else {
      // 检查是否刚刚登录，需要根据权限判断跳转目标
      const justLoggedIn = sessionStorage.getItem('JUST_LOGGED_IN')
      if (justLoggedIn) {
        sessionStorage.removeItem('JUST_LOGGED_IN')
        const redirectPath = getLoginRedirectPath()
        console.log('[Router] 刚登录，判断跳转:', redirectPath)
        if (redirectPath.startsWith('/bzmj')) {
          window.location.href = redirectPath
          nploading.done()
          return
        }
        if (redirectPath !== to.path && !to.path.startsWith(redirectPath)) {
          next({ path: redirectPath, query: to.query, replace: true })
          nploading.done()
          return
        }
      }

      // 检查用户是否有权限访问 /project 页面
      // 如果用户只有 bzmj 权限，应重定向到 /bzmj
      if (to.path.startsWith('/project') || to.path.startsWith('/recycle') || to.path.startsWith('/settings')) {
        const permissions = store.state.allPermissions || []
        const allProjectTags = permissions
          .filter(p => p.project)
          .flatMap(p => p.project.split(',').map(tag => tag.trim().toLowerCase()))
        const projectTagsSet = [...new Set(allProjectTags)]

        // 如果只有bzmj权限，无survey权限，则不允许访问/project
        if (projectTagsSet.includes('bzmj') && !projectTagsSet.includes('survey')) {
          console.log('[Router] 用户只有bzmj权限，重定向到/bzmj')
          window.location.href = '/bzmj'
          nploading.done()
          return
        }

        // 如果没有任何project标签但有bzmj相关的uri，也重定向
        if (projectTagsSet.length === 0) {
          const bzmjModule = permissions.find(p => p.uri && p.uri.includes('/bzmj'))
          if (bzmjModule) {
            console.log('[Router] 用户有bzmj模块权限，重定向到/bzmj')
            window.location.href = '/bzmj'
            nploading.done()
            return
          }
        }
      }

      // 更新登录过期时间
      api.updateExpireTime()
      next()
    }
  } else {
    // 无权限的情况下，先获取一下权限
    store
      .dispatch(UPDATE_USER_INFO)
      .then(() => {
        if (store.getters.userInfo.uid) {
          // 未添加过路由则添加之
          // 有权限则跳转
          if (hasAnyAuths()) {
            if (routeIsAdded === false) addRoutes()

            // 如果是从登录页来或访问根路径，根据权限判断跳转目标
            if (from.path === '/login' || to.path === '/') {
              const redirectPath = getLoginRedirectPath()
              console.log('[Router] Redirect to:', redirectPath)
              if (redirectPath.startsWith('/bzmj')) {
                // 跳转到bzmj需要使用location.href，因为是不同的应用
                window.location.href = redirectPath
                return
              }
              next({ path: redirectPath, query: to.query, replace: true })
            } else {
              next({ path: to.path || '/', query: to.query, replace: true })
            }
          } else {
            // 否则重定向到登录页面
            if (router.currentRoute.path !== '/login' && to.path !== '/login') {
              next({ path: '/login' })
            }
            throw new Error('该帐号无访问权限，请联系管理员')
          }
        } else {
          // 否则重定向到登录页面
          if (to.path !== '/login') {
            next({ path: '/login' })
            throw new Error('未登录系统')
          }
          next()
        }
        nploading.done()
      })
      .catch(err => {
        if (store.getters.userInfo.uid && err.message) {
          Message({
            type: 'warning',
            message: err.message
          })
        }
        nploading.done()
      })
  }
})

router.afterEach(() => nploading.done())

export default router
